import { useId } from 'react'

import { cn } from '@/lib/cn'

/**
 * BygOverblik-mærket: en stiliseret øjenform med tre bygninger, hvor den
 * midterste er højest – "byg" set gennem "overblik".
 *
 * Farverne kommer fra designtokens, ikke rå hex, så mærket følger med, hvis
 * paletten justeres. Gradient- og clip-id'er er unikke pr. instans, fordi
 * mærket kan optræde flere gange samtidig (sidebar + mobilmenu).
 *
 * Størrelsen styres udefra med en size-klasse: <LogoMark className="size-9" />
 */
export function LogoMark({ className }: { className?: string }) {
  const uid = useId().replace(/:/g, '')
  const eyeId = `logo-eye-${uid}`
  const markId = `logo-mark-${uid}`
  const irisId = `logo-iris-${uid}`

  return (
    <svg
      viewBox="0 0 32 32"
      role="img"
      aria-label="BygOverblik"
      className={cn('size-8 shrink-0', className)}
    >
      <defs>
        <linearGradient id={eyeId} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0%" stopColor="var(--color-app-700)" />
          <stop offset="100%" stopColor="var(--color-app-900)" />
        </linearGradient>
        <linearGradient id={markId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--color-brand-400)" />
          <stop offset="100%" stopColor="var(--color-info-500)" />
        </linearGradient>
        <clipPath id={irisId}>
          <circle cx="16" cy="16" r="6.75" />
        </clipPath>
      </defs>

      {/* Øjets ydre form */}
      <path
        d="M2 16C6 9.3 10.7 6 16 6s10 3.3 14 10c-4 6.7-8.7 10-14 10S6 22.7 2 16Z"
        fill={`url(#${eyeId})`}
        stroke="var(--color-brand-600)"
        strokeWidth="1.1"
        strokeLinejoin="round"
      />

      {/* Iris */}
      <circle cx="16" cy="16" r="6.75" fill="var(--color-app-950)" />

      {/* Tre bygninger – den midterste højest.
          Vinduesdetaljer er bevidst udeladt: ved 32 px ville de blive til støj. */}
      <g clipPath={`url(#${irisId})`} fill={`url(#${markId})`}>
        <rect x="11.6" y="15" width="2.5" height="7" rx="0.5" />
        <rect x="14.7" y="11.2" width="2.8" height="10.8" rx="0.6" />
        <rect x="18.1" y="13.7" width="2.5" height="8.3" rx="0.5" />
      </g>

      {/* Ring om irissen i brandgradienten */}
      <circle
        cx="16"
        cy="16"
        r="6.75"
        fill="none"
        stroke={`url(#${markId})`}
        strokeWidth="0.8"
        opacity="0.7"
      />
    </svg>
  )
}

export function Logo({ collapsed = false }: { collapsed?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <LogoMark />
      {!collapsed && (
        <span className="truncate text-[15px] font-semibold tracking-tight">BygOverblik</span>
      )}
    </div>
  )
}
